"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

import { getOrCreateBootstrapDistinctId } from "@/lib/analytics-identity";
import { sendOwnedAnalyticsEvent } from "@/lib/analytics-server";
import { readUtmParams } from "@/lib/utm-params";

declare global {
  interface Window {
    __ishOwnedPageViewPath?: string;
  }
}

/**
 * Immediate, first-party page-view coverage for Next routes.
 *
 * This intentionally lives outside the deferred analytics gates: it loads no
 * third-party script and writes only to the owned edge beacon. The pathname ref
 * prevents React Strict Mode/effect replays from duplicating a logical route
 * view while still allowing A -> B -> A navigation to record three views.
 */
export default function OwnedPageViewTracker() {
  const pathname = usePathname();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastTrackedPath.current === pathname) return;
    if (window.__ishOwnedPageViewPath === pathname) {
      lastTrackedPath.current = pathname;
      return;
    }

    const distinctId = getOrCreateBootstrapDistinctId();
    if (!distinctId) return;

    lastTrackedPath.current = pathname;
    window.__ishOwnedPageViewPath = pathname;
    // Attach utm_* when the landing carries them (recovery/trigger message links,
    // rule 55) so events_raw attributes which template/campaign drove the return.
    // Spread only present keys so ordinary page views stay clean.
    sendOwnedAnalyticsEvent("page_view", {
      distinct_id: distinctId,
      source_path: pathname,
      traffic_class: "human_candidate",
      ...readUtmParams(),
    });
  }, [pathname]);

  return null;
}
